import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { 
  MapPin, ExternalLink, Users, Building2, 
  GraduationCap, HandHeart, Loader2, Search, CheckCircle,
  Church, Briefcase, Heart
} from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';



// Organization categories matching backend ORG_TYPES
const ORG_CATEGORIES = [
  { value: 'nonprofit', label: 'Nonprofit', icon: HandHeart, color: 'text-green-500' },
  { value: 'community_group', label: 'Community Group', icon: Users, color: 'text-blue-500' },
  { value: 'school', label: 'School', icon: GraduationCap, color: 'text-yellow-500' },
  { value: 'church', label: 'Church', icon: Church, color: 'text-purple-500' },
  { value: 'youth_program', label: 'Youth Program', icon: Heart, color: 'text-pink-500' },
  { value: 'small_business', label: 'Small Business', icon: Briefcase, color: 'text-orange-500' },
  { value: 'other', label: 'Other', icon: Building2, color: 'text-gray-500' }
];

export default function CommunitySpotlightPage() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    city: '',
    neighborhood: '',
    search: ''
  });

  useEffect(() => {
    fetchOrganizations();
  }, [filters]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      let url = `${API}/organizations/spotlight?limit=50&all=true`;
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.neighborhood) url += `&neighborhood=${encodeURIComponent(filters.neighborhood)}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data.organizations || []);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return ORG_CATEGORIES.find(c => c.value === categoryValue) || ORG_CATEGORIES[6];
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="community-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <HandHeart className="w-8 h-8 text-emerald-500" />
            <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase">
              Community <span className="text-emerald-500">Spotlight</span>
            </h1>
          </div>
          <p className="text-muted-foreground text-base mb-1">
            Supporting Nonprofit Organizations in the DMV
          </p>
          <p className="text-muted-foreground/60 text-sm">
            Highlighting nonprofits, community programs, local initiatives, outreach events, and charity efforts.
          </p>
          <Link to="/spotlights-news" className="text-xs text-primary hover:underline mt-2 inline-block">
            Looking for artist spotlights & platform news? Go to Spotlights & News →
          </Link>
        </div>

        {/* Filters */}
        <div className="glass-card border-border p-4 rounded-xl mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 bg-muted/30 border-border"
                data-testid="community-search"
              />
            </div>

            {/* Category Filter */}
            <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-full lg:w-48 bg-muted/30 border-border" data-testid="community-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {ORG_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Neighborhood Filter */}
            <Select value={filters.neighborhood || 'all'} onValueChange={(v) => setFilters({ ...filters, neighborhood: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-full lg:w-48 bg-muted/30 border-border" data-testid="community-neighborhood-filter">
                <SelectValue placeholder="All Areas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                <SelectItem value="Southeast">Southeast DC</SelectItem>
                <SelectItem value="Northeast">Northeast DC</SelectItem>
                <SelectItem value="Northwest">Northwest DC</SelectItem>
                <SelectItem value="Southwest">Southwest DC</SelectItem>
                <SelectItem value="PG County">Prince George's County</SelectItem>
                <SelectItem value="Montgomery">Montgomery County</SelectItem>
                <SelectItem value="Baltimore">Baltimore</SelectItem>
                <SelectItem value="NoVA">Northern Virginia</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category Tags */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Badge
            variant={!filters.category ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setFilters({ ...filters, category: '' })}
          >
            All
          </Badge>
          {ORG_CATEGORIES.map(cat => (
            <Badge
              key={cat.value}
              variant={filters.category === cat.value ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setFilters({ ...filters, category: cat.value })}
            >
              <cat.icon className={`w-3 h-3 mr-1 ${cat.color}`} />
              {cat.label}
            </Badge>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : organizations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="community-grid">
            {organizations.map((org, index) => (
              <motion.div
                key={org.org_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <OrganizationCard org={org} getCategoryInfo={getCategoryInfo} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <HandHeart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No Organizations Found</h2>
            <p className="text-muted-foreground mb-6">
              {filters.search || filters.category || filters.neighborhood 
                ? "Try adjusting your filters to find more organizations."
                : "Community organizations will appear here once they join the platform."}
            </p>
            <Link to="/login">
              <Button>
                <Building2 className="w-4 h-4 mr-2" />
                Register Your Organization
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function OrganizationCard({ org, getCategoryInfo }) {
  const category = getCategoryInfo(org.org_type);
  const CategoryIcon = category.icon;

  return (
    <Card className="glass-card border-border overflow-hidden hover-lift h-full">
      <div className="p-6">
        {/* Header with logo and name */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-muted/50 overflow-hidden flex-shrink-0">
            {org.profile_image_url ? (
              <img src={org.profile_image_url} alt={org.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold truncate">{org.name}</h3>
              {org.verified && (
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">{org.handle ? `@${org.handle}` : ""}</p>
            <Badge className={`mt-1 ${category.color.replace('text-', 'bg-')}/20 ${category.color} text-xs`}>
              <CategoryIcon className="w-3 h-3 mr-1" />
              {category.label}
            </Badge>
          </div>
        </div>

        {/* Mission */}
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {org.mission}
        </p>

        {/* Location */}
        {(org.city || org.neighborhood) && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
            <MapPin className="w-4 h-4" />
            <span>{[org.neighborhood, org.city, org.state].filter(Boolean).join(', ')}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Link to={`/org/${org.handle}`} className="flex-1">
            <Button size="sm" className="w-full">View Profile</Button>
          </Link>
          {org.donate_url && (
            <a 
              href={org.donate_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button size="sm" variant="outline" className="w-full text-green-500 border-green-500/50 hover:bg-green-500/10">
                <ExternalLink className="w-3 h-3 mr-1" />
                Donate
              </Button>
            </a>
          )}
          {org.volunteer_url && (
            <a 
              href={org.volunteer_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button size="sm" variant="outline" className="w-full text-purple-500 border-purple-500/50 hover:bg-purple-500/10">
                <ExternalLink className="w-3 h-3 mr-1" />
                Volunteer
              </Button>
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}
